import { useEffect, useMemo, useState } from "react";
import { ClipboardCheck, Download, Eye, Pencil, Plus, Trash2, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../../services/apiService";
import { useAuth } from "../../../context/AuthContext";
import { Badge } from "../../../components/ops/Badge";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataTable } from "../../../components/ops/DataTable";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { DateTimeField } from "../../../components/ops/DateTimeField";
import { downloadCsv } from "../../../components/ops/exportCsv";
import { EmptyState } from "../../../components/ops/EmptyState";
import { Modal } from "../../../components/ops/Modal";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { SearchSelect } from "../../../components/ops/SearchSelect";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const ageGroupLabels = { "1-3": "Ages 1-3", "4-5": "Ages 4-5", "6-8": "Ages 6-8", "9-11": "Ages 9-11", "12-19": "Ages 12-19", adult: "Adults" };
const skillLevels = ["beginner", "developing", "competitive", "advanced"];
const feet = ["left", "right", "both"];

const emptyCreateForm = { parentId: "", firstName: "", lastName: "", dateOfBirth: null, skillLevel: "developing", preferredFoot: "right", goals: "", emergencyContactName: "", emergencyContactPhone: "" };
const emptyEditForm = { firstName: "", lastName: "", dateOfBirth: null, skillLevel: "developing", preferredFoot: "right", goals: "", emergencyContactName: "", emergencyContactPhone: "" };

function toPayload(form) {
  return {
    firstName: form.firstName,
    lastName: form.lastName,
    dateOfBirth: form.dateOfBirth.toISOString(),
    skillLevel: form.skillLevel,
    preferredFoot: form.preferredFoot,
    goals: form.goals,
    emergencyContact: { name: form.emergencyContactName, phone: form.emergencyContactPhone },
  };
}

export function AdminPlayersPage() {
  const { accessToken, user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [players, setPlayers] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [ageFilter, setAgeFilter] = useState("all");
  const [skillFilter, setSkillFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [creating, setCreating] = useState(false);

  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [savingEdit, setSavingEdit] = useState(false);

  const [viewingRow, setViewingRow] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");

    Promise.all([api.get("/players"), api.get("/dashboard/summary"), api.get("/players/parents")])
      .then(([playersResponse, summaryResponse, parentsResponse]) => {
        setPlayers(playersResponse.data.players || []);
        const map = {};
        (summaryResponse.data.players || []).forEach((entry) => { map[entry.player._id] = entry; });
        setMetrics(map);
        setParents(parentsResponse.data.parents || []);
      })
      .catch(() => setError("Unable to load players"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const parentOptions = useMemo(() => parents.map((p) => ({ value: p._id, label: `${p.firstName} ${p.lastName} (${p.email})` })), [parents]);

  const rows = useMemo(
    () => players.map((player) => ({ player, metric: metrics[player._id] })),
    [players, metrics],
  );

  const filteredRows = useMemo(() => {
    return rows.filter(({ player }) => {
      if (ageFilter !== "all" && player.ageGroup !== ageFilter) return false;
      if (skillFilter !== "all" && player.skillLevel !== skillFilter) return false;
      return true;
    });
  }, [rows, ageFilter, skillFilter]);

  const { page, setPage, totalPages, totalItems, pageItems } = usePagedList(filteredRows, {
    search,
    searchFields: ["player.firstName", "player.lastName"],
    pageSize: 10,
  });

  const openCreate = () => {
    setCreateForm(emptyCreateForm);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateForm(emptyCreateForm);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.parentId) {
      toast.error("Please select a parent for this player");
      return;
    }
    setCreating(true);
    try {
      const response = await api.post("/players", { ...toPayload(createForm), parentId: createForm.parentId });
      setPlayers([response.data.player, ...players]);
      toast.success("Player added successfully!");
      closeCreate();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to add player");
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (player) => {
    setEditingPlayer(player);
    setEditForm({
      firstName: player.firstName,
      lastName: player.lastName,
      dateOfBirth: new Date(player.dateOfBirth),
      skillLevel: player.skillLevel,
      preferredFoot: player.preferredFoot,
      goals: player.goals || "",
      emergencyContactName: player.emergencyContact?.name || "",
      emergencyContactPhone: player.emergencyContact?.phone || "",
    });
  };

  const closeEdit = () => {
    setEditingPlayer(null);
    setEditForm(emptyEditForm);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const response = await api.patch(`/players/${editingPlayer._id}`, toPayload(editForm));
      setPlayers(players.map((p) => (p._id === response.data.player._id ? response.data.player : p)));
      toast.success("Player updated successfully!");
      closeEdit();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to update player");
    } finally {
      setSavingEdit(false);
    }
  };

  const removePlayer = async (player) => {
    const ok = await confirm({
      title: "Delete this player?",
      message: `"${player.firstName} ${player.lastName}" will be permanently removed, along with their assessment and attendance history. This can't be undone.`,
      confirmLabel: "Delete player",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/players/${player._id}`);
      setPlayers(players.filter((p) => p._id !== player._id));
      toast.success("Player deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete player");
    }
  };

  const columns = [
    {
      key: "player",
      label: "Player",
      render: ({ player }) => (
        <div className="ops-table-player">
          <span className="ops-table-avatar">{player.firstName?.charAt(0)}{player.lastName?.charAt(0)}</span>
          {player.firstName} {player.lastName}
        </div>
      ),
    },
    { key: "ageGroup", label: "Age group", render: ({ player }) => player.ageGroup },
    { key: "skillLevel", label: "Skill level", render: ({ player }) => <span className="ops-table-capitalize">{player.skillLevel}</span> },
    { key: "preferredFoot", label: "Preferred foot", render: ({ player }) => <span className="ops-table-capitalize">{player.preferredFoot}</span> },
    { key: "attendance", label: "Attendance", render: ({ metric }) => (metric?.attendanceRate != null ? `${metric.attendanceRate}%` : "—") },
    {
      key: "fpi",
      label: "Formula Player Index",
      render: ({ metric }) => (
        metric?.formulaPlayerIndex != null
          ? <Badge tone="success">{metric.formulaPlayerIndex}</Badge>
          : <Badge tone="neutral">Not assessed</Badge>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Link to={`/${user?.role === "superadmin" ? "admin" : user?.role || "admin"}/players/${row.player._id}/report`} className="ops-button ops-button--outline ops-button--small" aria-label={`Open assessment report for ${row.player.firstName} ${row.player.lastName}`}>
            <ClipboardCheck size={13} /> <span className="player-report-action-label">Report</span>
          </Link>
          <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => setViewingRow(row)} aria-label="View player">
            <Eye size={13} />
          </button>
          <button type="button" className="ops-button ops-button--outline ops-button--small" onClick={() => openEdit(row.player)} aria-label="Edit player">
            <Pencil size={13} />
          </button>
          <button type="button" className="ops-button ops-button--danger ops-button--small" onClick={() => removePlayer(row.player)} aria-label="Delete player">
            <Trash2 size={13} />
          </button>
        </div>
      ),
    },
  ];

  const exportRoster = () => {
    downloadCsv(
      `players-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { label: "First name", value: ({ player }) => player.firstName },
        { label: "Last name", value: ({ player }) => player.lastName },
        { label: "Age group", value: ({ player }) => player.ageGroup },
        { label: "Skill level", value: ({ player }) => player.skillLevel },
        { label: "Preferred foot", value: ({ player }) => player.preferredFoot },
        { label: "Attendance %", value: ({ metric }) => metric?.attendanceRate ?? "" },
        { label: "Formula Player Index", value: ({ metric }) => metric?.formulaPlayerIndex ?? "" },
      ],
      filteredRows,
    );
  };

  return (
    <div>
      <PageHeader
        title="Players"
        subtitle="Full roster across all programs"
        actions={
          <>
            {rows.length > 0 && (
              <button type="button" className="ops-button ops-button--outline" onClick={exportRoster}>
                <Download size={14} /> Export CSV
              </button>
            )}
            <button type="button" className="ops-button ops-button--primary" onClick={openCreate}>
              <Plus size={16} /> New Player
            </button>
          </>
        }
      />

      <div className="ops-card">
        {error && <div className="ops-alert ops-alert--error">{error}</div>}

        <DataToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search players by name..."
          filters={[
            {
              label: "Age group",
              value: ageFilter,
              onChange: setAgeFilter,
              options: [{ value: "all", label: "All ages" }, ...Object.entries(ageGroupLabels).map(([value, label]) => ({ value, label }))],
            },
            {
              label: "Skill level",
              value: skillFilter,
              onChange: setSkillFilter,
              options: [{ value: "all", label: "All skill levels" }, ...skillLevels.map((level) => ({ value: level, label: level }))],
            },
          ]}
          resultCount={totalItems}
          itemLabel="players"
        />

        {loading ? (
          <p className="ops-loading">Loading players…</p>
        ) : totalItems === 0 ? (
          <EmptyState icon={Users} title="No players found" description={rows.length === 0 ? "Add your first player to get started." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <DataTable columns={columns} rows={pageItems} rowKey={(row) => row.player._id} />
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      <Modal
        open={createOpen}
        onClose={closeCreate}
        title="New Player"
        size="md"
        footer={
          <>
            <button type="submit" form="create-player-form" className="ops-button ops-button--primary" disabled={creating}>{creating ? "Adding..." : "Add Player"}</button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeCreate}>Cancel</button>
          </>
        }
      >
        <form id="create-player-form" onSubmit={submitCreate} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Family</p>
            <div className="ops-form-group" style={{ marginBottom: 0 }}>
              <label className="ops-form-label">Parent</label>
              <SearchSelect
                options={parentOptions}
                value={createForm.parentId}
                onChange={(value) => setCreateForm({ ...createForm, parentId: value })}
                placeholder="Search parents…"
                noOptionsMessage="No parent accounts found"
              />
            </div>
          </div>

          <div className="ops-form-section">
            <p className="ops-form-section-title">Player details</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">First name</label>
                <input required className="ops-input" value={createForm.firstName} onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Last name</label>
                <input required className="ops-input" value={createForm.lastName} onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} />
              </div>
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Date of birth</label>
              <DateTimeField
                required
                showTime={false}
                value={createForm.dateOfBirth}
                onChange={(date) => setCreateForm({ ...createForm, dateOfBirth: date })}
                placeholder="Select date of birth"
              />
            </div>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Skill level</label>
                <select className="ops-select" value={createForm.skillLevel} onChange={(e) => setCreateForm({ ...createForm, skillLevel: e.target.value })}>
                  {skillLevels.map((level) => <option key={level} value={level}>{level}</option>)}
                </select>
              </div>
              <div>
                <label className="ops-form-label">Preferred foot</label>
                <select className="ops-select" value={createForm.preferredFoot} onChange={(e) => setCreateForm({ ...createForm, preferredFoot: e.target.value })}>
                  {feet.map((foot) => <option key={foot} value={foot}>{foot}</option>)}
                </select>
              </div>
            </div>
            <div className="ops-form-group" style={{ marginBottom: 0 }}>
              <label className="ops-form-label">Goals <span className="ops-form-label-optional">(optional)</span></label>
              <textarea className="ops-textarea" placeholder="What is this player working toward?" value={createForm.goals} onChange={(e) => setCreateForm({ ...createForm, goals: e.target.value })} />
            </div>
          </div>

          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <p className="ops-form-section-title">Emergency contact <span className="ops-form-label-optional">(optional)</span></p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Name</label>
                <input className="ops-input" value={createForm.emergencyContactName} onChange={(e) => setCreateForm({ ...createForm, emergencyContactName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Phone</label>
                <input type="tel" className="ops-input" value={createForm.emergencyContactPhone} onChange={(e) => setCreateForm({ ...createForm, emergencyContactPhone: e.target.value })} />
              </div>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(editingPlayer)}
        onClose={closeEdit}
        title="Edit Player"
        size="md"
        footer={
          <>
            <button type="submit" form="edit-player-form" className="ops-button ops-button--primary" disabled={savingEdit}>{savingEdit ? "Saving..." : "Save Changes"}</button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeEdit}>Cancel</button>
          </>
        }
      >
        <form id="edit-player-form" onSubmit={submitEdit} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Player details</p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">First name</label>
                <input required className="ops-input" value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Last name</label>
                <input required className="ops-input" value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
              </div>
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Date of birth</label>
              <DateTimeField
                required
                showTime={false}
                value={editForm.dateOfBirth}
                onChange={(date) => setEditForm({ ...editForm, dateOfBirth: date })}
                placeholder="Select date of birth"
              />
            </div>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Skill level</label>
                <select className="ops-select" value={editForm.skillLevel} onChange={(e) => setEditForm({ ...editForm, skillLevel: e.target.value })}>
                  {skillLevels.map((level) => <option key={level} value={level}>{level}</option>)}
                </select>
              </div>
              <div>
                <label className="ops-form-label">Preferred foot</label>
                <select className="ops-select" value={editForm.preferredFoot} onChange={(e) => setEditForm({ ...editForm, preferredFoot: e.target.value })}>
                  {feet.map((foot) => <option key={foot} value={foot}>{foot}</option>)}
                </select>
              </div>
            </div>
            <div className="ops-form-group" style={{ marginBottom: 0 }}>
              <label className="ops-form-label">Goals <span className="ops-form-label-optional">(optional)</span></label>
              <textarea className="ops-textarea" value={editForm.goals} onChange={(e) => setEditForm({ ...editForm, goals: e.target.value })} />
            </div>
          </div>

          <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
            <p className="ops-form-section-title">Emergency contact <span className="ops-form-label-optional">(optional)</span></p>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Name</label>
                <input className="ops-input" value={editForm.emergencyContactName} onChange={(e) => setEditForm({ ...editForm, emergencyContactName: e.target.value })} />
              </div>
              <div>
                <label className="ops-form-label">Phone</label>
                <input type="tel" className="ops-input" value={editForm.emergencyContactPhone} onChange={(e) => setEditForm({ ...editForm, emergencyContactPhone: e.target.value })} />
              </div>
            </div>
          </div>
        </form>
      </Modal>

      <Modal open={Boolean(viewingRow)} onClose={() => setViewingRow(null)} title="Player Details" size="sm">
        {viewingRow && (
          <div className="ops-form">
            <div className="ops-form-section">
              <p className="ops-form-section-title">Profile</p>
              <p style={{ margin: "0 0 8px" }}><strong>Name:</strong> {viewingRow.player.firstName} {viewingRow.player.lastName}</p>
              <p style={{ margin: "0 0 8px" }}><strong>Age group:</strong> {ageGroupLabels[viewingRow.player.ageGroup] || viewingRow.player.ageGroup}</p>
              <p style={{ margin: "0 0 8px" }}><strong>Date of birth:</strong> {new Date(viewingRow.player.dateOfBirth).toLocaleDateString()}</p>
              <p style={{ margin: "0 0 8px" }}><strong>Skill level:</strong> <span className="ops-table-capitalize">{viewingRow.player.skillLevel}</span></p>
              <p style={{ margin: 0 }}><strong>Preferred foot:</strong> <span className="ops-table-capitalize">{viewingRow.player.preferredFoot}</span></p>
            </div>

            {viewingRow.player.goals && (
              <div className="ops-form-section">
                <p className="ops-form-section-title">Goals</p>
                <p style={{ margin: 0, color: "var(--ops-muted)" }}>{viewingRow.player.goals}</p>
              </div>
            )}

            {(viewingRow.player.emergencyContact?.name || viewingRow.player.emergencyContact?.phone) && (
              <div className="ops-form-section">
                <p className="ops-form-section-title">Emergency contact</p>
                <p style={{ margin: "0 0 8px" }}><strong>Name:</strong> {viewingRow.player.emergencyContact?.name || "—"}</p>
                <p style={{ margin: 0 }}><strong>Phone:</strong> {viewingRow.player.emergencyContact?.phone || "—"}</p>
              </div>
            )}

            <div className="ops-form-section" style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
              <p className="ops-form-section-title">Development</p>
              <p style={{ margin: "0 0 8px" }}>
                <strong>Attendance:</strong> {viewingRow.metric?.attendanceRate != null ? `${viewingRow.metric.attendanceRate}%` : "No attendance yet"}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Formula Player Index:</strong>{" "}
                {viewingRow.metric?.formulaPlayerIndex != null ? <Badge tone="success">{viewingRow.metric.formulaPlayerIndex}</Badge> : <Badge tone="neutral">Not assessed</Badge>}
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
